import React, { useState, useContext, useRef, useEffect } from "react";
import { useHistory, Link } from "react-router-dom";
import { Modal } from "react-bootstrap";

import { MyContext, ListContext } from "./MyContext";
import { PrimaryTextBlue, BlueButton } from "./styleCustom";
import { ModalHeaderBlue } from "./formComponents";
import COLORS from "./styleConstants";
import "./elementsStyle.css";
import Auth from "./Auth";

export const Succeeded = ({ currentPage = "purchases", onClose }) => {
  const { listState, dispatch2 } = useContext(ListContext);
  const history = useHistory();
  const finishPay = () => {
    if (onClose) onClose();
    history.push(
      `/admin/clienthome/${listState.currentClient.id}/${currentPage}`,
      `/admin/clienthome/${listState.currentClient.id}/${currentPage}`,
    );
  };

  return (
    <div
      style={{
        margin: 24,
        display: "flex",
        flexDirection: "row",
        flexWrap: "wrap",
        justifyContent: "center",
      }}
      className={"paymentSuccess"}
    >
      <div>
        <PrimaryTextBlue>Your payment was successful.</PrimaryTextBlue>
      </div>
      <div>
        <PrimaryTextBlue>
          A detailed receipt has been sent to your email.
        </PrimaryTextBlue>
      </div>
      <BlueButton style={{ marginTop: 30 }} onClick={() => finishPay()}>
        close
      </BlueButton>
    </div>
  );
};

export const PaymentButtons = ({
  processing,
  onCancel,
  paymentConfirmed = false,
  checkingPaymentStatus = false,
  onCheckPaymentStatus,
}) => {
  const { listState, dispatch2 } = useContext(ListContext);
  const { mystate, dispatch } = useContext(MyContext);

  const cancelPaymentIntent = async () => {
    const paymentIntentId = listState.paymentIntent?.id;
    dispatch2(["SET_INVOICE_PAYMENT", {}]);
    if (!paymentIntentId) return;
    const params = { payment_intent_id: paymentIntentId };
    await Auth.api("payments/cancelPaymentIntent/", "post", params, {
      skipGlobalErrorHandler: true,
    });
  };
  return (
    <>
      {!paymentConfirmed && (
        <button
          type="submit"
          disabled={processing}
          style={{ width: "100%", background: COLORS.orange }}
          className="payment"
        >
          {processing
            ? "Processing…"
            : `pay  ${mystate.farmDefaults.currency_code.toLocaleUpperCase()}
            ${
              mystate.farmDefaults.currency_symbol
            }${listState.cartTotal.toLocaleString(navigator.language, {
              minimumFractionDigits: 2,
            })}`}
        </button>
      )}
      {paymentConfirmed ? (
        <button
          type="button"
          onClick={onCheckPaymentStatus}
          disabled={checkingPaymentStatus}
          style={{ width: "100%", background: COLORS.blue, color: "white" }}
          className="payment"
        >
          {checkingPaymentStatus
            ? "Checking payment status…"
            : "Check payment status"}
        </button>
      ) : (
        <button
          type="button"
          style={{ width: "100%", background: COLORS.blue, color: "white" }}
          className="payment"
        >
          <Link
            style={{
              display: "block",
              color: "white",
              background: COLORS.blue,
            }}
            onClick={(event) => {
              if (processing) {
                event.preventDefault();
                return;
              }
              cancelPaymentIntent().catch((error) => console.error(error));
              if (onCancel) onCancel();
            }}
            to={`/admin/clienthome/${listState.absoluteCurrentClientId}/online`}
          >
            cancel payment
          </Link>
        </button>
      )}
    </>
  );
};

/**
 * Shared checkout modal for both saved-card and new-card payments.
 * Children receive { setProcessing, setError, onConfirmed } and call
 * onConfirmed(reference) after a successful confirm to show the success flow.
 */
const CheckoutModal = ({
  children,
  isInvoicePayment = false,
  paymentId = "",
  currentPage = "purchases",
}) => {
  const { listState, dispatch2 } = useContext(ListContext);
  const { mystate, dispatch } = useContext(MyContext);
  const [error, setError] = useState(null);
  const [succeeded, setSucceeded] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [confirmedPaymentIntentId, setConfirmedPaymentIntentId] =
    useState(null);
  const [checkingPaymentStatus, setCheckingPaymentStatus] = useState(false);
  const [paymentStatusNotice, setPaymentStatusNotice] = useState("");
  const fulfillmentNoticeTimerRef = useRef(null);
  // The active payment form registers its async submit handler here so the
  // wrapper's <form> and pay button can trigger it.
  const submitRef = useRef(null);

  useEffect(
    () => () => {
      if (fulfillmentNoticeTimerRef.current) {
        clearTimeout(fulfillmentNoticeTimerRef.current);
      }
    },
    [],
  );

  const clearFulfillmentNoticeTimer = () => {
    if (fulfillmentNoticeTimerRef.current) {
      clearTimeout(fulfillmentNoticeTimerRef.current);
      fulfillmentNoticeTimerRef.current = null;
    }
  };

  const clearCheckoutState = () => {
    dispatch2(["CLEAR_CART"]);
    dispatch2(["SET_CLIENT_PAYMENT", {}]);
    dispatch2(["SET_INVOICE_PAYMENT", {}]);
  };

  const finishPay = () => {
    dispatch({ type: "FETCH_SUCCESS" });
    setSucceeded(true);
  };

  const onFulfilled = () => {
    setError(null);
    setProcessing(false);
    finishPay();
  };

  const getPaymentAttemptStatus = async (paymentIntentId) => {
    if (!navigator.onLine) {
      throw new Error(
        "You are offline. Reconnect and check the payment status again.",
      );
    }

    try {
      const result = await Auth.api(
        "payments/paymentAttemptStatus",
        "get",
        {
          payment_intent_id: paymentIntentId,
        },
        {
          timeout: 5000,
          skipGetRetry: true,
          skipWaitForStableOnline: true,
          skipGlobalErrorHandler: true,
        },
      );
      return result.data.status;
    } catch (error) {
      // No attempt row yet means fulfillment is still pending.
      if (error.response?.status === 404) return "pending";
      throw error;
    }
  };

  const checkPaymentStatus = async (paymentIntentId) => {
    if (!paymentIntentId || checkingPaymentStatus) return;
    setCheckingPaymentStatus(true);
    setPaymentStatusNotice(
      "Payment confirmed by Stripe. Finalizing your purchase…",
    );
    setError(null);
    try {
      const status = await getPaymentAttemptStatus(paymentIntentId);

      if (status === "fulfilled") {
        setPaymentStatusNotice("");
        setProcessing(false);
        finishPay();
      } else if (status === "pending") {
        setPaymentStatusNotice(
          "Stripe confirmed your payment, but purchase setup is still pending. Do not pay again. Check the status again shortly.",
        );
      } else {
        setPaymentStatusNotice(
          "Stripe confirmed the payment, but purchase setup reported a problem. Do not pay again; contact support to resolve it.",
        );
      }
    } catch (error) {
      setPaymentStatusNotice(
        "Stripe confirmed your payment, but its purchase status could not be checked. Do not pay again; check again shortly or contact support.",
      );
    } finally {
      setCheckingPaymentStatus(false);
      setProcessing(false);
    }
  };

  // Stripe has confirmed the card; ask Rails to fulfill before relying on webhook recovery.
  const onConfirmed = async (paymentIntentId) => {
    setConfirmedPaymentIntentId(paymentIntentId);
    dispatch({ type: "FETCH_SUCCESS" });
    setProcessing(true);
    setPaymentStatusNotice("");
    setError(null);
    clearFulfillmentNoticeTimer();
    fulfillmentNoticeTimerRef.current = setTimeout(() => {
      setPaymentStatusNotice(
        "Payment confirmed by Stripe. Recording your purchase…",
      );
      fulfillmentNoticeTimerRef.current = null;
    }, 1000);

    try {
      const result = await Auth.api(
        "payments/createPaymentFromStripe",
        "post",
        {
          transaction_type: "online",
          payment_intent_id: paymentIntentId,
        },
        { skipGlobalErrorHandler: true },
      );

      if (
        result?.data?.status === "fulfilled" &&
        result.data.payment_intent_id === paymentIntentId
      ) {
        clearFulfillmentNoticeTimer();
        setPaymentStatusNotice("");
        finishPay();
        return;
      }

      clearFulfillmentNoticeTimer();
      setPaymentStatusNotice(
        "Stripe confirmed your payment, but purchase fulfillment is still pending. Do not pay again; check the status shortly.",
      );
    } catch (error) {
      clearFulfillmentNoticeTimer();
      setPaymentStatusNotice(
        "Stripe confirmed your payment, but we could not confirm purchase fulfillment. Do not pay again; check the status shortly.",
      );
    } finally {
      setProcessing(false);
    }
  };

  return (
    <>
      <Modal show={true} size="md" centered backdrop="static">
        <ModalHeaderBlue>
          <Modal.Title>Card Payment</Modal.Title>
        </ModalHeaderBlue>
        <Modal.Body style={{ padding: 24 }}>
          {!succeeded && (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (confirmedPaymentIntentId) return;
                if (submitRef.current) submitRef.current(e);
              }}
              className="payment"
              style={{
                width: "100%",
                maxWidth: 500,
                display: "flex",
                flexDirection: "column",
                margin: "0 auto",
              }}
            >
              {typeof children === "function"
                ? children({
                    processing,
                    setProcessing,
                    error,
                    setError,
                    onConfirmed,
                    onFulfilled,
                    submitRef,
                  })
                : children}
              {error && (
                <div className="message sr-field-error checkout-payment-error">
                  {error}
                </div>
              )}
              {paymentStatusNotice && (
                <div role="status" className="message">
                  {paymentStatusNotice}
                </div>
              )}
              <PaymentButtons
                processing={processing}
                onCancel={clearCheckoutState}
                paymentConfirmed={Boolean(confirmedPaymentIntentId)}
                checkingPaymentStatus={checkingPaymentStatus}
                onCheckPaymentStatus={() =>
                  checkPaymentStatus(confirmedPaymentIntentId)
                }
              />
            </form>
          )}
          {succeeded && (
            <Succeeded currentPage={currentPage} onClose={clearCheckoutState} />
          )}
        </Modal.Body>
      </Modal>
    </>
  );
};

export default CheckoutModal;
